// src/components/meeting/MobileMeetingDetailCard.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { format } from "date-fns";
import api from "../../api/axios";
import { softDeleteDoc } from "../../api/papers";
import { meetingApi } from "../../api/meeting";
import ParticipantStatusCard from "../common/feature/ParticipantStatusCard";
import TmapGeocoder from "../viewer/tmap/TmapGeocoder";
import FileList from "../common/papers/FileList";

// 모바일 카드 스타일
const cardBaseClass =
  "w-full max-w-md mx-auto rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden min-w-0";
const labelClassView = "mb-1 text-xs text-slate-600";
const valueCls =
  "text-sm text-slate-900 leading-6 min-w-0 break-words whitespace-pre-wrap";
const btnClass =
  "px-3 py-2 rounded-xl border bg-white text-slate-900 text-sm hover:bg-slate-50 whitespace-nowrap";

const isUrl = (v = "") => /^https?:\/\//i.test(v);

export default function MobileMeetingDetailCard({
  item,
  projectId,
  onEdit,
  onDeleted,
}) {
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: item?.title ? `[회의] ${item.title}` : "회의 상세",
    pageStyle: `
      @page { size: A4; margin: 14mm; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
  });
  const [deleting, setDeleting] = useState(false);

  const [users, setUsers] = useState([]);
  const effectiveProjectId = useMemo(
    () => projectId ?? item?.project,
    [projectId, item?.project]
  );
  useEffect(() => {
    if (!effectiveProjectId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/project/${effectiveProjectId}/users-without-me/`
        );
        setUsers(data?.results ?? data ?? []);
      } catch { }
    })();
  }, [effectiveProjectId]);

  const userById = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(Number(u.id), u);
    return m;
  }, [users]);

  const remove = async () => {
    if (!item?.id) {
      alert("삭제할 회의 ID가 없습니다. 관리자에게 문의해주세요.");
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("이 회의를 삭제하시겠습니까?")) return;
    try {
      setDeleting(true);
      await meetingApi.remove(item.id);
      if (item?.dns) {
        try {
          await softDeleteDoc(item.dns, true);
        } catch (e) {
          console.error("문서 soft-delete 실패(무시):", e);
        }
      }
      onDeleted?.();
      alert("회의가 삭제되었습니다.");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || e?.message || "알 수 없는 오류";
      alert(`삭제 실패: ${msg}`);
    } finally {
      setDeleting(false);
    }
  };

  const rawLoc = item?.location || "";
  const locType =
    item?.location_type ||
    (isUrl(rawLoc) ? "online" : rawLoc ? "offline_address" : "offline_address");
  const zoomUrl = locType === "online" ? rawLoc : "";
  const addr = locType === "offline_address" ? rawLoc : "";
  const roomName = locType === "offline_room" ? rawLoc : "";

  return (
    <div className={`${cardBaseClass}`}>
      <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2 min-w-0">
        <h4 className="text-base font-semibold truncate">상세 회의</h4>
        <div className="flex flex-wrap items-center gap-2 no-print">
          <button className={btnClass} onClick={handlePrint}>
            인쇄
          </button>
          <button className={btnClass} onClick={() => onEdit?.()}>
            수정
          </button>
          <button className={btnClass} onClick={remove} disabled={deleting}>
            {deleting ? "삭제 중…" : "삭제"}
          </button>
        </div>
      </div>

      <div ref={printRef} className="px-4 py-4 space-y-5 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={labelClassView}>제목</div>
            {item?.is_urgent && (
              <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700">
                긴급
              </span>
            )}
          </div>
          <div className={valueCls}>{item?.title || "-"}</div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={labelClassView}>날짜 및 시간</div>
            {item?.recurrence_label && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
                {item.recurrence_label}
              </span>
            )}
          </div>
          <div className={valueCls}>
            {item?.schedule
              ? format(new Date(item.schedule), "yyyy-MM-dd HH:mm")
              : "-"}
          </div>
        </div>

        <div className="min-w-0">
          <div className="text-sm text-slate-600 mb-1">장소</div>

          <div className="mb-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-700">
              {locType === "online"
                ? "온라인"
                : locType === "offline_room"
                  ? "오프라인(회의실)"
                  : "오프라인(주소)"}
            </span>
          </div>

          {locType === "online" && (
            <div className="min-w-0">
              <div className="text-sm text-slate-600 mb-1">회의 링크</div>
              {zoomUrl ? (
                <a
                  href={
                    zoomUrl.startsWith("http") ? zoomUrl : `https://${zoomUrl}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm underline underline-offset-4 text-indigo-700 break-all"
                >
                  {zoomUrl}
                </a>
              ) : (
                <div className={valueCls}>-</div>
              )}
            </div>
          )}

          {locType === "offline_room" && (
            <>
              <div className="text-sm text-slate-600 mb-1">회의실</div>
              <div className={`${valueCls} break-words`}>{roomName || "-"}</div>
            </>
          )}

          {locType === "offline_address" && (
            <>
              <div className="text-sm text-slate-600 mb-1">주소</div>
              <div className={`${valueCls} break-words`}>{addr || "-"}</div>

              <div className="mt-2 rounded-xl overflow-hidden border border-slate-200">
                <TmapGeocoder
                  address={addr}
                  mapId={`meeting-location-map-view-${item?.id}`}
                  onGeocode={() => { }}
                />
              </div>
            </>
          )}
        </div>

        {!!(item?.participants || []).length && (
          <section className="min-w-0">
            <div className={labelClassView}>참석자</div>

            <div className="flex flex-wrap gap-2.5 mt-1">
              {(item?.participants || []).map((p) => {
                const u = userById.get(Number(p.user_id));
                const fullName = u
                  ? `${u.last_name}${u.first_name}`
                  : (p.user_last_name || "") + (p.user_first_name || "") ||
                  p.user ||
                  "-";
                const position = u?.position || "";

                return (
                  <ParticipantStatusCard
                    key={p.user_id}
                    name={fullName}
                    position={position}
                    responded={!!p.responded}
                    isAttending={!!p.is_attending}
                  />
                );
              })}
            </div>

            {item?.response_summary && (
              <div className="mt-2 text-xs text-slate-600">
                응답 현황: 전체 {item.response_summary.total}, 응답(참여){" "}
                {item.response_summary.attending}, 불참{" "}
                {item.response_summary.not_attending}
              </div>
            )}
          </section>
        )}

        <div className="min-w-0">
          <div className={labelClassView}>안건</div>
          <div className={valueCls}>{item?.agenda || "-"}</div>
        </div>

        <div className="min-w-0">
          <div className={labelClassView}>결과</div>
          <div className={valueCls}>{item?.result || "-"}</div>
        </div>

        <section className="min-w-0">
          <div className={labelClassView}>첨부</div>
          {item?.dns ? (
            <FileList dns={item.dns} showTitle={false} />
          ) : (
            <p className="text-sm text-slate-500">
              문서 DNS 정보가 없어 첨부를 표시할 수 없습니다.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

